import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Survey } from 'survey-react-ui';
import 'survey-core/defaultV2.min.css';
import '../styles/smart-form.less';
import { ReferralHelpNote } from '../components/ReferralHelpNote';
import { SmartFormChat } from '../components/SmartFormChat';
import { extractAttachments, stripAttachmentContent, toRawBase64, } from '../lib/attachments';
import { DOCUMENT_PARSE_ACCEPT, DOCUMENT_PARSE_FILE_HINT, DOCUMENT_PARSE_UNSUPPORTED_MESSAGE, detectDocumentParseKind, isAllowedDocumentParseFile, } from '../lib/documentParseFile';
import { runDocumentParse, runSmartFill, SmartFillError, } from '../services/smartFill';
import { getOrLoadFormSession, getValidFormSession, invalidateFormSession, } from '../services/formSession';
import { ensureSignInUserInfo } from '../services/signinUserInfo';
import { extractIncidentId, fetchPortalForms, submitFormReferral, uploadReferralAttachment, } from '../services/workflow';
import { createReferralAttachedForm, fetchReferralDetail, snapshotReferralFormPdf, updateReferralAttachedForm, } from '../services/referrals';
import { OPEN_STATUS } from '../lib/referralTimeLimits';
import { isReferralLinkChecked, setReferralLinkChecked, } from '../lib/supportingFormLinks';
import { bindSurvey, executePostSubmit, executeValidateThenSubmit, resetFafBindings, } from '../faf/fafHost';
function contactKey(firstName, lastName, email) {
    return `${firstName}|${lastName}|${email}`.trim().toLowerCase();
}
function toPersonInvolved(contact) {
    return {
        id: 0,
        incidentId: 0,
        type: 4,
        kind: 0,
        canModify: false,
        firstName: contact.firstName.trim(),
        lastName: contact.lastName.trim(),
        email: contact.email.trim() || null,
        gender: null,
    };
}
async function bindFafForForm(formId, survey, incidentId) {
    await bindSurvey({
        formId,
        survey,
        host: {
            formId: Number(formId) || formId,
            incidentId: incidentId ?? null,
            pageUrl: typeof window !== 'undefined' ? window.location.href : null,
        },
    });
}
async function snapshotSavedFormsPdf(incidentId, forms) {
    let ok = 0;
    let failed = 0;
    for (const form of forms) {
        try {
            await snapshotReferralFormPdf({
                incidentId,
                fileName: form.fileName,
                formId: form.formId ?? null,
                surveyJson: form.surveyJson ?? null,
                formData: form.formData,
            });
            ok += 1;
        }
        catch (err) {
            failed += 1;
            console.error('[form-pdf-snapshot] client failed', {
                incidentId,
                fileName: form.fileName,
                formId: form.formId ?? null,
                error: err instanceof Error ? err.message : String(err),
            });
        }
    }
    return { ok, failed };
}
async function runFafPostSubmit(referralSurvey, referralFormId, openIds) {
    try {
        if (referralSurvey && referralFormId) {
            await executePostSubmit(referralFormId, referralSurvey);
        }
        for (const formId of openIds) {
            if (formId === referralFormId)
                continue;
            const session = getValidFormSession(formId);
            if (session) {
                await executePostSubmit(formId, session.survey);
            }
        }
    }
    catch (err) {
        console.error('[FAF] formPostSubmit failed', err);
    }
}
export function NewReferralPage({ editReferralId } = {}) {
    const navigate = useNavigate();
    const isEditMode = typeof editReferralId === 'number' && editReferralId > 0;
    const surveyContainerRef = useRef(null);
    const [modeTab, setModeTab] = useState('form');
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [preparingSubmit, setPreparingSubmit] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [portalForms, setPortalForms] = useState([]);
    const [openFormIds, setOpenFormIds] = useState([]);
    const [activeFormId, setActiveFormId] = useState(null);
    const [formsCatalogError, setFormsCatalogError] = useState('');
    const [addFormMenuOpen, setAddFormMenuOpen] = useState(false);
    const addFormMenuRef = useRef(null);
    const [formLoading, setFormLoading] = useState(true);
    const [formError, setFormError] = useState('');
    const [, setFormTitle] = useState('');
    const [survey, setSurvey] = useState(null);
    const [formDefinition, setFormDefinition] = useState(null);
    const [assistantMode, setAssistantMode] = useState('docked');
    const assistantPlaceRef = useRef('docked');
    const [surveyPageNo, setSurveyPageNo] = useState(0);
    const [pendingFillResult, setPendingFillResult] = useState(null);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [pendingSupportingForms, setPendingSupportingForms] = useState([]);
    const [readyToSubmit, setReadyToSubmit] = useState(false);
    const [foundContacts, setFoundContacts] = useState([]);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [editBootstrapping, setEditBootstrapping] = useState(isEditMode);
    const editSeedsRef = useRef({});
    const appliedEditSeedsRef = useRef(new Set());
    const reviewSectionRef = useRef(null);
    const preparingSubmitRef = useRef(false);
    const formScrollPositions = useRef({});
    const pendingScrollRestoreId = useRef(null);
    const linkSyncingRef = useRef(false);
    const openFormIdsRef = useRef(openFormIds);
    const portalFormsRef = useRef(portalForms);
    const addSupportingFormRef = useRef(() => undefined);
    const removeSupportingFormRef = useRef(() => undefined);
    openFormIdsRef.current = openFormIds;
    portalFormsRef.current = portalForms;
    // Drop cached SurveyJS sessions / smart-form chat so this visit starts blank.
    // Form definition cache is kept so the empty form still loads quickly.
    useEffect(() => {
        invalidateFormSession();
        resetFafBindings();
    }, []);
    const activeForm = portalForms.find((form) => form.formId === activeFormId) ?? null;
    const isReferralForm = activeForm?.kind === 'referral';
    const referralForm = portalForms.find((form) => form.kind === 'referral') ?? null;
    const openForms = openFormIds
        .map((id) => portalForms.find((form) => form.formId === id))
        .filter((form) => Boolean(form));
    const availableSupportingForms = portalForms.filter((form) => form.kind === 'supporting' && !openFormIds.includes(form.formId));
    const getReferralSurvey = useCallback(() => {
        if (!referralForm)
            return null;
        const session = getValidFormSession(referralForm.formId);
        if (session)
            return session.survey;
        if (isReferralForm && survey)
            return survey;
        return null;
    }, [referralForm, isReferralForm, survey]);
    const syncReferralLink = useCallback((formId, checked) => {
        const form = portalFormsRef.current.find((entry) => entry.formId === formId);
        if (!form?.link)
            return;
        const referralSurvey = getReferralSurvey();
        if (!referralSurvey)
            return;
        linkSyncingRef.current = true;
        try {
            setReferralLinkChecked(referralSurvey, form.link, checked);
        }
        finally {
            // SurveyJS may fire onValueChanged synchronously.
            queueMicrotask(() => {
                linkSyncingRef.current = false;
            });
        }
    }, [getReferralSurvey]);
    const selectForm = useCallback((formId) => {
        if (formId === activeFormId)
            return;
        if (activeFormId) {
            formScrollPositions.current[activeFormId] = window.scrollY;
        }
        pendingScrollRestoreId.current = formId;
        setActiveFormId(formId);
        setAddFormMenuOpen(false);
    }, [activeFormId]);
    const addSupportingForm = useCallback((formId, options = {}) => {
        const activate = options.activate !== false;
        setOpenFormIds((prev) => {
            const next = prev.includes(formId) ? prev : [...prev, formId];
            openFormIdsRef.current = next;
            return next;
        });
        if (activate) {
            selectForm(formId);
        }
        else {
            setAddFormMenuOpen(false);
        }
        syncReferralLink(formId, true);
    }, [selectForm, syncReferralLink]);
    const removeSupportingForm = useCallback((formId) => {
        const form = portalForms.find((entry) => entry.formId === formId);
        if (!form || form.kind !== 'supporting')
            return;
        delete formScrollPositions.current[formId];
        syncReferralLink(formId, false);
        setOpenFormIds((prev) => {
            const next = prev.filter((id) => id !== formId);
            openFormIdsRef.current = next;
            if (activeFormId === formId) {
                const referral = portalForms.find((entry) => entry.kind === 'referral');
                const fallbackId = referral?.formId ?? next[0] ?? null;
                if (fallbackId) {
                    pendingScrollRestoreId.current = fallbackId;
                    setActiveFormId(fallbackId);
                }
                else {
                    setActiveFormId(null);
                }
            }
            return next;
        });
    }, [activeFormId, portalForms, syncReferralLink]);
    addSupportingFormRef.current = addSupportingForm;
    removeSupportingFormRef.current = removeSupportingForm;
    const handleContactsFound = useCallback((contacts) => {
        setFoundContacts((prev) => {
            const seen = new Set(prev.map((c) => contactKey(c.firstName, c.lastName, c.email)));
            const additions = [];
            for (const contact of contacts) {
                const email = contact.email ?? '';
                const key = contactKey(contact.firstName, contact.lastName, email);
                if (seen.has(key))
                    continue;
                seen.add(key);
                additions.push({
                    key: `contact-${Date.now()}-${additions.length}-${Math.random().toString(36).slice(2)}`,
                    firstName: contact.firstName,
                    lastName: contact.lastName,
                    email,
                    note: contact.note,
                    include: true,
                });
            }
            return additions.length ? [...prev, ...additions] : prev;
        });
    }, []);
    const updateContactText = useCallback((key, field, value) => {
        setFoundContacts((prev) => prev.map((c) => (c.key === key ? { ...c, [field]: value } : c)));
    }, []);
    const toggleContact = useCallback((key, include) => {
        setFoundContacts((prev) => prev.map((c) => (c.key === key ? { ...c, include } : c)));
    }, []);
    // "Prepare for submit" validates via SurveyJS, then runs one smart-fill pass so
    // AI can extract people involved before the review / Submit Referral step.
    const handleSurveyCompleting = useCallback((sender, options) => {
        options.allowComplete = false;
        if (preparingSubmitRef.current || !formDefinition)
            return;
        preparingSubmitRef.current = true;
        setError('');
        setPreparingSubmit(true);
        setPendingAttachments(extractAttachments(sender.data));
        // Snapshot open supporting forms now so the review card stays accurate even
        // if link-field smart-fill briefly mutates referral answers.
        setPendingSupportingForms(openFormIdsRef.current
            .map((formId) => portalFormsRef.current.find((entry) => entry.formId === formId))
            .filter((form) => form != null && form.kind === 'supporting'));
        const details = 'From the completed referral form data, identify all people who should be involved in this referral (patient/claimant, employer contacts, physicians, adjusters, and any other contacts). Return contactInformationFound for each person with firstName, lastName, email when available, and a short note describing their role. Also fill any missing form fields you can confidently determine.';
        void (async () => {
            let fillQueued = false;
            let fafStopped = false;
            try {
                const fafCtx = await executeValidateThenSubmit(referralForm?.formId ?? activeFormId, sender);
                if (fafCtx?.shouldStop) {
                    fafStopped = true;
                    return;
                }
                const result = await runSmartFill({
                    details,
                    formDefinition: JSON.parse(JSON.stringify(formDefinition)),
                    currentData: sender.data,
                    conversationHistory: [],
                });
                fillQueued = true;
                setPendingFillResult({
                    result,
                    userMessage: 'Prepare for submit — identify people involved',
                });
            }
            catch (err) {
                console.error('[prepare-submit] smart fill failed', err);
                setError(err instanceof SmartFillError || err instanceof Error
                    ? `Could not auto-detect people involved: ${err.message}`
                    : 'Could not auto-detect people involved.');
            }
            finally {
                setPreparingSubmit(false);
                if (fafStopped) {
                    preparingSubmitRef.current = false;
                    return;
                }
                setReadyToSubmit(true);
                // Keep preparingSubmitRef true until pending fill is applied (if any),
                // so link-sync does not react mid-apply.
                if (!fillQueued) {
                    preparingSubmitRef.current = false;
                }
                setTimeout(() => {
                    reviewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 50);
            }
        })();
    }, [formDefinition, referralForm, activeFormId]);
    const handleFinalSubmit = useCallback(async () => {
        if (!survey || !isReferralForm)
            return;
        setError('');
        setSubmitting(true);
        const attachments = extractAttachments(survey.data);
        const cleanData = stripAttachmentContent(survey.data);
        console.log('[referral submit] SurveyJS data (stripped)', cleanData);
        console.log('[referral submit] attachments', attachments.map((a) => a.name));
        try {
            if (isEditMode && editReferralId) {
                const referralSeed = referralForm
                    ? editSeedsRef.current[referralForm.formId]
                    : undefined;
                if (!referralSeed?.attachedId) {
                    throw new Error('Could not find the attached referral form to update.');
                }
                await updateReferralAttachedForm({
                    id: referralSeed.attachedId,
                    formData: cleanData,
                    formTitle: referralSeed.formTitle,
                    formId: referralSeed.formId,
                    incidentId: editReferralId,
                    survey: formDefinition && typeof formDefinition === 'object'
                        ? formDefinition
                        : null,
                });
                const pdfForms = [
                    {
                        fileName: referralSeed.formTitle || referralForm?.name || 'Referral',
                        formId: referralSeed.formId ?? referralForm?.formId ?? null,
                        surveyJson: formDefinition && typeof formDefinition === 'object' ? formDefinition : null,
                        formData: cleanData,
                    },
                ];
                let supportingUpdated = 0;
                let supportingCreated = 0;
                for (const formId of openFormIds) {
                    const meta = portalForms.find((entry) => entry.formId === formId);
                    if (!meta || meta.kind !== 'supporting')
                        continue;
                    const { detail, survey: supportingSurvey } = await getOrLoadFormSession(formId);
                    const surveyJson = detail.survey && typeof detail.survey === 'object' && !Array.isArray(detail.survey)
                        ? detail.survey
                        : null;
                    const rawData = supportingSurvey.data &&
                        typeof supportingSurvey.data === 'object' &&
                        !Array.isArray(supportingSurvey.data)
                        ? supportingSurvey.data
                        : {};
                    const formData = stripAttachmentContent(rawData);
                    const seed = editSeedsRef.current[formId];
                    const formIdNum = Number(formId);
                    if (seed?.attachedId) {
                        await updateReferralAttachedForm({
                            id: seed.attachedId,
                            formData,
                            formTitle: seed.formTitle || meta.name,
                            formId: seed.formId ?? (Number.isFinite(formIdNum) ? formIdNum : null),
                            incidentId: editReferralId,
                            survey: surveyJson,
                        });
                        supportingUpdated += 1;
                    }
                    else if (Number.isFinite(formIdNum)) {
                        await createReferralAttachedForm({
                            incidentId: editReferralId,
                            formId: formIdNum,
                            formTitle: meta.name,
                            survey: surveyJson,
                            formData,
                        });
                        supportingCreated += 1;
                    }
                    else {
                        continue;
                    }
                    pdfForms.push({
                        fileName: seed?.formTitle || meta.name,
                        formId: seed?.formId ?? (Number.isFinite(formIdNum) ? formIdNum : formId),
                        surveyJson,
                        formData,
                    });
                }
                let attachmentNote = '';
                if (attachments.length > 0) {
                    const outcomes = await Promise.allSettled(attachments.map((attachment) => uploadReferralAttachment({
                        caseId: editReferralId,
                        fileName: attachment.name,
                        content: toRawBase64(attachment.content),
                    })));
                    const failed = outcomes.filter((o) => o.status === 'rejected').length;
                    const uploaded = outcomes.length - failed;
                    attachmentNote =
                        failed > 0
                            ? ` Uploaded ${uploaded} of ${outcomes.length} new attachment(s); ${failed} failed.`
                            : ` ${uploaded} new attachment(s) uploaded.`;
                }
                const pdfResult = await snapshotSavedFormsPdf(editReferralId, pdfForms);
                if (pdfResult.ok > 0 || pdfResult.failed > 0) {
                    attachmentNote +=
                        pdfResult.failed > 0
                            ? ` Form PDF(s): ${pdfResult.ok} attached, ${pdfResult.failed} failed.`
                            : ` ${pdfResult.ok} form PDF(s) attached.`;
                }
                const supportingNote = supportingUpdated + supportingCreated > 0
                    ? ` ${supportingUpdated} supporting form(s) updated${supportingCreated > 0 ? `, ${supportingCreated} added` : ''}.`
                    : '';
                await runFafPostSubmit(survey, referralForm?.formId ?? activeFormId, openFormIds);
                setSuccess(`Referral updated successfully.${supportingNote}${attachmentNote}`);
                setReadyToSubmit(false);
                setPendingAttachments([]);
                setPendingSupportingForms([]);
                setFoundContacts([]);
                invalidateFormSession();
                resetFafBindings();
                setTimeout(() => navigate(`/referrals/${editReferralId}`), 1200);
                return;
            }
            const incidentPersonInvolved = foundContacts
                .filter((c) => c.include && (c.firstName.trim() || c.lastName.trim()))
                .map(toPersonInvolved);
            console.log('[referral submit] incidentPersonInvolved', incidentPersonInvolved);
            const additionalForms = [];
            for (const formId of openFormIds) {
                const meta = portalForms.find((entry) => entry.formId === formId);
                if (!meta || meta.kind !== 'supporting')
                    continue;
                const { detail, survey: supportingSurvey } = await getOrLoadFormSession(formId);
                const surveyJson = detail.survey && typeof detail.survey === 'object' && !Array.isArray(detail.survey)
                    ? detail.survey
                    : null;
                const rawData = supportingSurvey.data &&
                    typeof supportingSurvey.data === 'object' &&
                    !Array.isArray(supportingSurvey.data)
                    ? supportingSurvey.data
                    : {};
                additionalForms.push({
                    formId,
                    formTitle: meta.name,
                    survey: surveyJson,
                    formData: stripAttachmentContent(rawData),
                });
            }
            console.log('[referral submit] additionalForms', additionalForms.map((f) => ({ formId: f.formId, keys: Object.keys(f.formData).length })));
            const userInfo = await ensureSignInUserInfo();
            const submitter = {
                userId: userInfo.detail.id,
                firstName: userInfo.detail.firstName ?? '',
                lastName: userInfo.detail.lastName ?? '',
                email: userInfo.detail.email ?? '',
            };
            console.log('[referral submit] submitter', submitter);
            const incident = await submitFormReferral(cleanData, incidentPersonInvolved, submitter, additionalForms);
            console.log('[referral submit] success', incident);
            const caseId = extractIncidentId(incident);
            console.log('[referral submit] resolved caseId', caseId);
            let attachmentNote = '';
            if (attachments.length > 0) {
                if (caseId == null) {
                    console.warn('[referral submit] could not resolve incident id; attachments not uploaded');
                    attachmentNote =
                        ' (but the referral id could not be determined, so attachments were not uploaded)';
                }
                else {
                    const outcomes = await Promise.allSettled(attachments.map((attachment) => uploadReferralAttachment({
                        caseId,
                        fileName: attachment.name,
                        content: toRawBase64(attachment.content),
                    })));
                    const failed = outcomes.filter((o) => o.status === 'rejected').length;
                    const uploaded = outcomes.length - failed;
                    console.log('[referral attachments] uploaded', uploaded, 'failed', failed);
                    attachmentNote =
                        failed > 0
                            ? ` Uploaded ${uploaded} of ${outcomes.length} attachment(s); ${failed} failed.`
                            : ` ${uploaded} attachment(s) uploaded.`;
                }
            }
            if (caseId != null) {
                const pdfForms = [
                    {
                        fileName: referralForm?.name || 'Referral',
                        formId: referralForm?.formId ?? null,
                        surveyJson: formDefinition && typeof formDefinition === 'object' ? formDefinition : null,
                        formData: cleanData,
                    },
                    ...additionalForms.map((form) => ({
                        fileName: form.formTitle,
                        formId: form.formId,
                        surveyJson: form.survey,
                        formData: form.formData,
                    })),
                ];
                const pdfResult = await snapshotSavedFormsPdf(caseId, pdfForms);
                if (pdfResult.ok > 0 || pdfResult.failed > 0) {
                    attachmentNote +=
                        pdfResult.failed > 0
                            ? ` Form PDF(s): ${pdfResult.ok} attached, ${pdfResult.failed} failed.`
                            : ` ${pdfResult.ok} form PDF(s) attached.`;
                }
            }
            const supportingNote = additionalForms.length > 0
                ? ` ${additionalForms.length} supporting form(s) attached.`
                : '';
            await runFafPostSubmit(survey, referralForm?.formId ?? activeFormId, openFormIds);
            setSuccess(`Referral submitted successfully.${supportingNote}${attachmentNote}`);
            setReadyToSubmit(false);
            setPendingAttachments([]);
            setPendingSupportingForms([]);
            setFoundContacts([]);
            invalidateFormSession();
            resetFafBindings();
            setTimeout(() => navigate('/'), 1800);
        }
        catch (err) {
            console.error('[referral submit] failed', err);
            setError(err instanceof Error
                ? err.message
                : isEditMode
                    ? 'Failed to update referral. Please try again.'
                    : 'Failed to submit referral. Please try again.');
        }
        finally {
            setSubmitting(false);
        }
    }, [
        survey,
        navigate,
        foundContacts,
        isReferralForm,
        openFormIds,
        portalForms,
        isEditMode,
        editReferralId,
        referralForm,
        activeFormId,
        formDefinition,
    ]);
    const loadFormCatalog = useCallback(async () => {
        setFormsCatalogError('');
        try {
            const forms = await fetchPortalForms();
            setPortalForms(forms);
            const referral = forms.find((form) => form.kind === 'referral');
            const referralId = referral?.formId ?? forms[0]?.formId ?? null;
            // Edit mode: bootstrap effect owns open/active form ids after detail loads.
            if (isEditMode) {
                return;
            }
            setOpenFormIds((current) => {
                const stillValid = current.filter((id) => forms.some((form) => form.formId === id));
                if (stillValid.length > 0)
                    return stillValid;
                return referralId ? [referralId] : [];
            });
            setActiveFormId((current) => {
                if (current && forms.some((form) => form.formId === current)) {
                    return current;
                }
                return referralId;
            });
        }
        catch (err) {
            setFormsCatalogError(err instanceof Error ? err.message : 'Failed to load available forms');
        }
    }, [isEditMode]);
    useEffect(() => {
        if (!addFormMenuOpen)
            return;
        const onPointerDown = (event) => {
            if (!addFormMenuRef.current?.contains(event.target)) {
                setAddFormMenuOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape')
                setAddFormMenuOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [addFormMenuOpen]);
    const loadForm = useCallback(async (formId, force = false) => {
        setFormError('');
        const applyEditSeed = (model) => {
            const seed = editSeedsRef.current[formId];
            if (!seed || appliedEditSeedsRef.current.has(formId))
                return;
            model.data = { ...(seed.formData ?? {}) };
            appliedEditSeedsRef.current.add(formId);
        };
        if (force) {
            appliedEditSeedsRef.current.delete(formId);
            invalidateFormSession(formId, true);
            resetFafBindings(formId);
        }
        const cached = !force ? getValidFormSession(formId) : null;
        if (cached) {
            const formMeta = portalForms.find((form) => form.formId === formId);
            const isMainReferral = formMeta?.kind === 'referral';
            cached.survey.widthMode = 'responsive';
            cached.survey.completeText = 'Prepare for submit';
            cached.survey.showCompleteButton = Boolean(isMainReferral);
            applyEditSeed(cached.survey);
            await bindFafForForm(formId, cached.survey, isEditMode ? editReferralId : null);
            setFormTitle(cached.detail.title);
            setFormDefinition(cached.detail.survey);
            setSurvey(cached.survey);
            setFormLoading(false);
            return;
        }
        setFormLoading(true);
        // Keep the previous survey mounted until the next form is ready so a failed
        // load (or quick tab switch) does not flash an empty form.
        try {
            const formMeta = portalForms.find((form) => form.formId === formId);
            const { detail, survey: model } = await getOrLoadFormSession(formId, {
                allowSubmit: formMeta?.kind === 'referral',
            });
            applyEditSeed(model);
            await bindFafForForm(formId, model, isEditMode ? editReferralId : null);
            setFormTitle(detail.title);
            setFormDefinition(detail.survey);
            setSurvey(model);
        }
        catch (err) {
            setFormError(err instanceof Error ? err.message : 'Failed to load form from workflow engine');
        }
        finally {
            setFormLoading(false);
        }
    }, [portalForms, isEditMode, editReferralId]);
    useEffect(() => {
        loadFormCatalog();
    }, [loadFormCatalog]);
    // Edit mode: load referral detail, seed form answers, open matching supporting forms.
    useEffect(() => {
        if (!isEditMode || !editReferralId) {
            setEditBootstrapping(false);
            return;
        }
        if (portalForms.length === 0)
            return;
        let cancelled = false;
        setEditBootstrapping(true);
        void (async () => {
            try {
                const detail = await fetchReferralDetail(editReferralId);
                if (cancelled)
                    return;
                if (detail.status !== OPEN_STATUS) {
                    setError('This referral can only be edited while status is Received.');
                    navigate(`/referrals/${editReferralId}`, { replace: true });
                    return;
                }
                const referral = portalForms.find((form) => form.kind === 'referral');
                const seeds = {};
                const nextOpenIds = [];
                if (referral) {
                    nextOpenIds.push(referral.formId);
                }
                for (const form of detail.issueForms ?? []) {
                    if (!form.canUpdate || form.id <= 0)
                        continue;
                    const catalogId = form.formId != null && Number.isFinite(form.formId)
                        ? String(form.formId)
                        : null;
                    if (!catalogId)
                        continue;
                    const catalog = portalForms.find((entry) => entry.formId === catalogId);
                    if (!catalog)
                        continue;
                    seeds[catalog.formId] = {
                        attachedId: form.id,
                        formId: form.formId ?? null,
                        formTitle: form.formTitle,
                        formData: form.formData ?? {},
                    };
                    if (catalog.kind === 'supporting' && !nextOpenIds.includes(catalog.formId)) {
                        nextOpenIds.push(catalog.formId);
                    }
                }
                // Fallback: attach the primary referral seed by kind if formId didn't match.
                if (referral && !seeds[referral.formId]) {
                    const main = detail.issueForms?.find((form) => form.canUpdate && form.id > 0) ?? null;
                    if (main) {
                        seeds[referral.formId] = {
                            attachedId: main.id,
                            formId: main.formId ?? (Number(referral.formId) || null),
                            formTitle: main.formTitle,
                            formData: main.formData ?? {},
                        };
                    }
                }
                if (referral && !seeds[referral.formId]) {
                    throw new Error('No updatable attached referral form was found on this referral.');
                }
                editSeedsRef.current = seeds;
                appliedEditSeedsRef.current = new Set();
                setExistingAttachments(detail.attachments ?? []);
                // Prefill every open form session so unopened supporting tabs still save answers.
                for (const formId of nextOpenIds) {
                    const meta = portalForms.find((entry) => entry.formId === formId);
                    const { survey: model } = await getOrLoadFormSession(formId, {
                        allowSubmit: meta?.kind === 'referral',
                    });
                    if (cancelled)
                        return;
                    const seed = seeds[formId];
                    if (seed) {
                        model.data = { ...(seed.formData ?? {}) };
                        appliedEditSeedsRef.current.add(formId);
                    }
                    await bindFafForForm(formId, model, editReferralId);
                }
                openFormIdsRef.current = nextOpenIds;
                setOpenFormIds(nextOpenIds);
                setActiveFormId(referral?.formId ?? nextOpenIds[0] ?? null);
            }
            catch (err) {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load referral for editing.');
                }
            }
            finally {
                if (!cancelled)
                    setEditBootstrapping(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isEditMode, editReferralId, portalForms, navigate]);
    useEffect(() => {
        if (!activeFormId || editBootstrapping)
            return;
        loadForm(activeFormId);
    }, [activeFormId, loadForm, editBootstrapping]);
    // Keep referral form linked to supporting-form tabs (check ↔ add).
    useEffect(() => {
        if (!referralForm)
            return;
        let cancelled = false;
        let referralSurvey = null;
        const onValueChanged = (_sender, options) => {
            if (linkSyncingRef.current || !referralSurvey)
                return;
            const changedName = options.name ?? '';
            const shortName = changedName.includes('.')
                ? changedName.slice(changedName.lastIndexOf('.') + 1)
                : changedName;
            for (const form of portalFormsRef.current) {
                if (form.kind !== 'supporting' || !form.link)
                    continue;
                if (form.link.fieldName !== shortName && form.link.fieldName !== changedName) {
                    continue;
                }
                const checked = isReferralLinkChecked(referralSurvey, form.link);
                const isOpen = openFormIdsRef.current.includes(form.formId);
                if (checked && !isOpen) {
                    addSupportingFormRef.current(form.formId, { activate: false });
                }
                // Do not auto-remove when the link is unchecked. Smart-fill / prepare-submit
                // often rewrites linked fields and would wipe open supporting tabs. Users
                // remove tabs explicitly via the × control (which also unchecks the link).
            }
        };
        void (async () => {
            try {
                const { survey: model } = await getOrLoadFormSession(referralForm.formId, {
                    allowSubmit: true,
                });
                if (cancelled)
                    return;
                referralSurvey = model;
                // If the referral already has linked docs checked, open those tabs.
                setOpenFormIds((prev) => {
                    let next = prev;
                    for (const form of portalFormsRef.current) {
                        if (form.kind !== 'supporting' || !form.link)
                            continue;
                        if (!isReferralLinkChecked(model, form.link))
                            continue;
                        if (next.includes(form.formId))
                            continue;
                        next = next === prev ? [...prev, form.formId] : [...next, form.formId];
                    }
                    openFormIdsRef.current = next;
                    return next;
                });
                model.onValueChanged.add(onValueChanged);
            }
            catch (err) {
                console.error('[supporting-form-link] failed to bind referral survey', err);
            }
        })();
        return () => {
            cancelled = true;
            referralSurvey?.onValueChanged.remove(onValueChanged);
        };
    }, [referralForm]);
    useEffect(() => {
        if (!survey)
            return;
        if (isReferralForm) {
            survey.completeText = 'Prepare for submit';
            survey.showCompleteButton = true;
            survey.showNavigationButtons = 'none';
            survey.showProgressBar = 'off';
            survey.onCompleting.add(handleSurveyCompleting);
            return () => {
                survey.onCompleting.remove(handleSurveyCompleting);
            };
        }
        // Supporting forms: never show Prepare for submit / complete.
        survey.showCompleteButton = false;
        survey.showNavigationButtons = 'none';
        survey.showProgressBar = 'off';
    }, [survey, handleSurveyCompleting, isReferralForm]);
    useEffect(() => {
        if (!survey)
            return;
        const syncPage = () => setSurveyPageNo(survey.currentPageNo);
        syncPage();
        survey.onCurrentPageChanged.add(syncPage);
        return () => {
            survey.onCurrentPageChanged.remove(syncPage);
        };
    }, [survey]);
    // Keep the prepare-submit supporting-forms review list in sync with open tabs.
    useEffect(() => {
        if (!readyToSubmit)
            return;
        setPendingSupportingForms(openFormIds
            .map((formId) => portalForms.find((entry) => entry.formId === formId))
            .filter((form) => form != null && form.kind === 'supporting'));
    }, [readyToSubmit, openFormIds, portalForms]);
    const formReady = !editBootstrapping &&
        !formLoading &&
        !formError &&
        survey &&
        formDefinition &&
        activeFormId;
    // Restore this form's scroll after the survey mounts (header stays sticky).
    useLayoutEffect(() => {
        if (!formReady || !activeFormId)
            return;
        if (pendingScrollRestoreId.current !== activeFormId)
            return;
        pendingScrollRestoreId.current = null;
        const y = formScrollPositions.current[activeFormId] ?? 0;
        window.scrollTo({ top: y, left: 0, behavior: 'auto' });
    }, [formReady, activeFormId, survey]);
    async function handleUpload(e) {
        e.preventDefault();
        if (!survey || !formDefinition || !isReferralForm) {
            setError(isReferralForm
                ? 'Form is still loading. Please wait and try again.'
                : 'Document parse applies to the referral form. Switch to Referral first.');
            return;
        }
        if (!file) {
            setError('Choose a file to upload before submitting.');
            return;
        }
        try {
            detectDocumentParseKind(file.name);
        }
        catch (err) {
            setError(err instanceof Error
                ? err.message
                : DOCUMENT_PARSE_UNSUPPORTED_MESSAGE);
            return;
        }
        setError('');
        setSubmitting(true);
        const userMessage = `Read the document: 
    ${file.name}, I will try to fill out the form 
    with the information found in the document.`;
        try {
            const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    if (typeof reader.result === 'string')
                        resolve(reader.result);
                    else
                        reject(new Error('Failed to read file as base64.'));
                };
                reader.onerror = () => reject(reader.error ?? new Error('Failed to read file.'));
                reader.readAsDataURL(file);
            });
            const result = await runDocumentParse({
                formDefinition: JSON.parse(JSON.stringify(formDefinition)),
                currentData: survey.data,
                conversationHistory: [],
                fileName: file.name,
                contents: toRawBase64(dataUrl),
            });
            setPendingFillResult({ result, userMessage });
            setModeTab('form');
        }
        catch (err) {
            console.error('[document-parse] failed', err);
            setError(err instanceof SmartFillError
                ? err.message
                : err instanceof Error
                    ? err.message
                    : 'Failed to parse document. Please try again.');
        }
        finally {
            setSubmitting(false);
        }
    }
    return (_jsxs("div", { className: "page page--new-referral", children: [_jsxs("div", { className: "new-referral-chrome", children: [_jsxs("div", { className: "page-header", children: [_jsxs("div", { children: [_jsx("h1", { children: isEditMode ? `Edit Referral ${editReferralId}` : 'New Referral' }), _jsx("p", { className: "page-subtitle", children: isEditMode
                                            ? 'Update the referral form, then prepare to save'
                                            : 'Complete the form below to refer an employee or dependent for behavioral health services' })] }), _jsx(ReferralHelpNote, {})] }), _jsxs("div", { className: "tab-bar new-referral-mode", role: "tablist", "aria-label": "How to complete the referral", children: [_jsx("button", { type: "button", className: `tab ${modeTab === 'form' ? 'active' : ''}`, onClick: () => setModeTab('form'), children: "Fill out form" }), _jsx("button", { type: "button", className: `tab ${modeTab === 'upload' ? 'active' : ''}`, onClick: () => setModeTab('upload'), children: "Upload document" })] }), modeTab === 'form' &&
                        portalForms.length > 0 &&
                        (openForms.length > 1 || availableSupportingForms.length > 0) && (_jsxs("div", { className: "form-switcher", role: "tablist", "aria-label": "Forms", children: [openForms.map((form) => (_jsxs("div", { className: `form-switcher-item${form.formId === activeFormId ? ' active' : ''}`, children: [_jsx("button", { type: "button", role: "tab", "aria-selected": form.formId === activeFormId, className: "form-switcher-tab", onClick: () => selectForm(form.formId), children: form.name }), form.kind === 'supporting' && (_jsx("button", { type: "button", className: "form-switcher-remove", "aria-label": `Remove ${form.name}`, title: "Remove form", onClick: (event) => {
                                            event.stopPropagation();
                                            removeSupportingForm(form.formId);
                                        }, children: "\u00D7" }))] }, form.formId))), availableSupportingForms.length > 0 && (_jsxs("div", { className: "add-form-menu", ref: addFormMenuRef, children: [_jsx("button", { type: "button", className: `form-switcher-tab form-switcher-tab--add${addFormMenuOpen ? ' is-open' : ''}`, "aria-expanded": addFormMenuOpen, "aria-haspopup": "listbox", onClick: () => setAddFormMenuOpen((open) => !open), children: "+ Add supporting form" }), addFormMenuOpen && (_jsx("ul", { className: "add-form-menu-list", role: "listbox", "aria-label": "Supporting forms", children: availableSupportingForms.map((form) => (_jsx("li", { role: "option", children: _jsx("button", { type: "button", className: "add-form-menu-option", onClick: () => addSupportingForm(form.formId), children: form.name }) }, form.formId))) }))] }))] }))] }), error && _jsx("div", { className: "alert alert-error", children: error }), success && _jsx("div", { className: "alert alert-success", children: success }), formsCatalogError && (_jsxs("div", { className: "form-load-error", children: [_jsx("p", { children: formsCatalogError }), _jsx("button", { type: "button", className: "btn btn-secondary", onClick: () => loadFormCatalog(), children: "Retry" })] })), formLoading && modeTab === 'form' && _jsx("div", { className: "table-empty", children: "Loading form\u2026" }), editBootstrapping && modeTab === 'form' && (_jsx("div", { className: "table-empty", children: "Loading referral\u2026" })), formError && modeTab === 'form' && (_jsxs("div", { className: "form-load-error", children: [_jsx("p", { children: formError }), _jsx("button", { type: "button", className: "btn btn-secondary", onClick: () => activeFormId && loadForm(activeFormId, true), children: "Retry" })] })), formReady && modeTab === 'form' && (_jsxs("div", { className: `new-referral-workspace${assistantMode === 'docked' ? '' : ' is-assistant-hidden'}`, children: [_jsxs("div", { className: "new-referral-form", children: [assistantMode === 'hidden' && (_jsx("button", { type: "button", className: "assistant-show-fab", onClick: () => setAssistantMode(assistantPlaceRef.current), children: "Show assistant" })), survey && survey.pageCount > 1 && (_jsx("ol", { className: "referral-steps", "aria-label": "Form progress", children: survey.pages.map((page, index) => {
                                    const title = (typeof page.title === 'string' && page.title.trim()) ||
                                        page.name ||
                                        `Step ${index + 1}`;
                                    const state = index === surveyPageNo ? 'is-current' : index < surveyPageNo ? 'is-done' : '';
                                    return (_jsx("li", { className: `referral-step ${state}`, children: _jsxs("button", { type: "button", className: "referral-step-btn", onClick: () => {
                                                survey.currentPageNo = index;
                                            }, children: [_jsx("span", { className: "referral-step-index", children: index + 1 }), _jsx("span", { className: "referral-step-label", children: title })] }) }, page.name || index));
                                }) })), _jsxs("section", { className: "survey-panel-side survey-panel-full", children: [(submitting || preparingSubmit) && (_jsx("div", { className: "overlay-msg overlay-msg--viewport", role: "status", "aria-live": "polite", children: _jsxs("div", { className: "overlay-msg-content", children: [_jsx("div", { className: "spinner", "aria-hidden": "true" }), _jsx("span", { children: preparingSubmit ? 'Detecting people involved…' : 'Submitting…' })] }) })), _jsx("div", { ref: surveyContainerRef, children: _jsx(Survey, { model: survey }) })] }), _jsxs("div", { className: "new-referral-nav", children: [_jsx("span", { className: "new-referral-draft", children: "Draft saved automatically" }), _jsxs("div", { className: "new-referral-nav-actions", children: [_jsx("button", { type: "button", className: "btn btn-secondary", disabled: !survey || survey.isFirstPage, onClick: () => survey?.prevPage(), children: "Back" }), _jsx("button", { type: "button", className: "btn btn-primary", disabled: !survey || preparingSubmit || submitting, onClick: () => {
                                                    if (!survey)
                                                        return;
                                                    if (survey.isLastPage) {
                                                        if (isReferralForm)
                                                            survey.completeLastPage();
                                                        return;
                                                    }
                                                    survey.nextPage();
                                                }, children: survey?.isLastPage ? 'Review' : 'Next' })] })] }), isReferralForm && readyToSubmit && !success && (_jsxs("div", { className: "submit-review", ref: reviewSectionRef, children: [_jsx("h2", { className: "submit-review-title", children: isEditMode ? 'Review & save' : 'Review & submit' }), isEditMode && existingAttachments.length > 0 && (_jsxs("div", { className: "review-card attachment-confirm", children: [_jsxs("h3", { className: "attachment-confirm-title", children: [existingAttachments.length, " existing attachment", existingAttachments.length === 1 ? '' : 's'] }), _jsx("ul", { className: "attachment-list", children: existingAttachments.map((attachment) => (_jsxs("li", { className: "attachment-item", children: [_jsx("span", { className: "attachment-check", "aria-hidden": "true", children: "\u2713" }), _jsx("span", { className: "attachment-name", children: attachment.name })] }, attachment.id))) }), _jsx("p", { className: "attachment-note", children: "Already on this referral. New files from the form will be added below." })] })), foundContacts.length > 0 && !isEditMode && (_jsxs("div", { className: "review-card contacts-review", children: [_jsxs("h3", { className: "review-card-title", children: ["Contacts found (", foundContacts.length, ")"] }), _jsx("p", { className: "review-card-note", children: "Review and edit each contact. Uncheck anyone you don't want added to this referral." }), _jsx("ul", { className: "contact-list", children: foundContacts.map((contact) => (_jsxs("li", { className: `contact-item${contact.include ? '' : ' is-excluded'}`, children: [_jsxs("label", { className: "contact-include", children: [_jsx("input", { type: "checkbox", checked: contact.include, onChange: (e) => toggleContact(contact.key, e.target.checked) }), _jsx("span", { className: "visually-hidden", children: "Include this contact" })] }), _jsxs("div", { className: "contact-fields", children: [_jsx("input", { className: "contact-input", placeholder: "First name", value: contact.firstName, onChange: (e) => updateContactText(contact.key, 'firstName', e.target.value) }), _jsx("input", { className: "contact-input", placeholder: "Last name", value: contact.lastName, onChange: (e) => updateContactText(contact.key, 'lastName', e.target.value) }), _jsx("input", { className: "contact-input", placeholder: "Email", value: contact.email, onChange: (e) => updateContactText(contact.key, 'email', e.target.value) }), contact.note && (_jsx("span", { className: "contact-note", children: contact.note }))] })] }, contact.key))) })] })), pendingAttachments.length > 0 && (_jsxs("div", { className: "review-card attachment-confirm", children: [_jsxs("h3", { className: "attachment-confirm-title", children: [pendingAttachments.length, " file", pendingAttachments.length === 1 ? '' : 's', " ready to attach"] }), _jsx("ul", { className: "attachment-list", children: pendingAttachments.map((attachment) => (_jsxs("li", { className: "attachment-item", children: [_jsx("span", { className: "attachment-check", "aria-hidden": "true", children: "\u2713" }), _jsx("span", { className: "attachment-name", children: attachment.name })] }, attachment.fieldPath))) }), _jsx("p", { className: "attachment-note", children: isEditMode
                                                    ? 'These files will be added as new attachments to this referral.'
                                                    : 'These files will be created as attachments to this referral.' })] })), pendingSupportingForms.length > 0 && (_jsxs("div", { className: "review-card attachment-confirm supporting-forms-confirm", children: [_jsxs("h3", { className: "attachment-confirm-title", children: [pendingSupportingForms.length, " supporting form", pendingSupportingForms.length === 1 ? '' : 's', " ready to", ' ', isEditMode ? 'save' : 'attach'] }), _jsx("ul", { className: "attachment-list", children: pendingSupportingForms.map((form) => (_jsxs("li", { className: "attachment-item", children: [_jsx("span", { className: "attachment-check", "aria-hidden": "true", children: "\u2713" }), _jsx("span", { className: "attachment-name", children: form.name })] }, form.formId))) }), _jsx("p", { className: "attachment-note", children: isEditMode
                                                    ? 'These supporting forms will be updated or added on this referral.'
                                                    : 'These forms will also be attached to this referral.' })] })), (!isEditMode || existingAttachments.length === 0) &&
                                        foundContacts.length === 0 &&
                                        pendingAttachments.length === 0 &&
                                        pendingSupportingForms.length === 0 && (_jsx("p", { className: "attachment-note", children: isEditMode
                                            ? 'Your referral updates are ready to save.'
                                            : 'Your referral is ready to submit.' })), _jsx("div", { className: "attachment-actions", children: _jsx("button", { type: "button", className: "btn btn-primary", onClick: handleFinalSubmit, disabled: submitting, children: submitting
                                                ? isEditMode
                                                    ? 'Saving…'
                                                    : 'Submitting…'
                                                : isEditMode
                                                    ? 'Save changes'
                                                    : 'Submit Referral' }) })] }))] }), assistantMode !== 'hidden' && (_jsx(SmartFormChat, { docked: assistantMode === 'docked', onHide: () => {
                            assistantPlaceRef.current = assistantMode;
                            setAssistantMode('hidden');
                        }, onToggleDock: () => {
                            setAssistantMode((mode) => {
                                const next = mode === 'docked' ? 'floating' : 'docked';
                                assistantPlaceRef.current = next;
                                return next;
                            });
                        }, persistenceKey: activeFormId ?? undefined, survey: survey, formDefinition: formDefinition, surveyContainerRef: surveyContainerRef, pendingFillResult: isReferralForm ? pendingFillResult : null, onPendingFillHandled: () => {
                            setPendingFillResult(null);
                            queueMicrotask(() => {
                                preparingSubmitRef.current = false;
                            });
                        }, onContactsFound: isReferralForm ? handleContactsFound : undefined }, activeFormId))] })), modeTab === 'upload' && (_jsxs("form", { className: "upload-panel", onSubmit: handleUpload, children: [_jsxs("div", { className: "upload-dropzone", children: [_jsx("input", { type: "file", id: "referral-file", accept: DOCUMENT_PARSE_ACCEPT, onChange: (e) => {
                                    const next = e.target.files?.[0] ?? null;
                                    if (next && !isAllowedDocumentParseFile(next.name)) {
                                        setFile(null);
                                        e.target.value = '';
                                        setError(DOCUMENT_PARSE_UNSUPPORTED_MESSAGE);
                                        return;
                                    }
                                    setError('');
                                    setFile(next);
                                } }), _jsx("label", { htmlFor: "referral-file", children: file ? (_jsxs(_Fragment, { children: [_jsx("strong", { children: file.name }), _jsxs("span", { children: [(file.size / 1024).toFixed(1), " KB"] })] })) : (_jsxs(_Fragment, { children: [_jsx("strong", { children: "Choose a file" }), _jsx("span", { children: DOCUMENT_PARSE_FILE_HINT })] })) })] }), _jsx("p", { className: "upload-test-note", children: "The selected file is uploaded to the S3 cache folder, then parsed to fill the referral form." }), _jsxs("div", { className: "upload-actions", children: [_jsx("button", { type: "button", className: "btn btn-ghost", onClick: () => navigate('/'), children: "Cancel" }), _jsx("button", { type: "submit", className: "btn btn-primary", disabled: submitting || formLoading || Boolean(formError) || !isReferralForm || !file, children: submitting ? 'Uploading & parsing…' : 'Submit document' })] })] }))] }));
}
